import { dequal } from "dequal/lite"
import React from "react"
import { type ZodType } from "zod/v4"

interface BrowserStoreApi<T> {
  initialValue: T
  schema: ZodType<T>
  key: string
  storage: Storage
  overwriteCurrentState?: boolean
}

type StoreUpdate<T> = T | ((currentValue: T) => T)

class BrowserStore<T> {
  readonly #key: string
  readonly #storage: Storage
  readonly #schema: ZodType<T>

  #value: T

  constructor(args: BrowserStoreApi<T>) {
    this.#key = args.key
    this.#storage = args.storage
    this.#schema = args.schema
    this.#value = args.initialValue
    this.#init({
      initialValue: args.initialValue,
      overwriteCurrentState: args.overwriteCurrentState,
    })
  }

  #init(args: { initialValue: T; overwriteCurrentState?: boolean }) {
    if (args.overwriteCurrentState) {
      this.#storage.setItem(this.#key, JSON.stringify(args.initialValue))
    } else {
      const storageValue = this.#storage.getItem(this.#key)
      const value = this.#validateStorageValue(storageValue)
      this.#value = value
      this.#storage.setItem(this.#key, JSON.stringify(value))
    }
  }

  #validateStorageValue = (storageValue: string | null) => {
    let value = this.#value
    try {
      if (storageValue) {
        value = this.#schema.parse(JSON.parse(storageValue))
      }
    } catch (e) {
      // oxlint-disable-next-line no-console
      console.error(
        "STORAGE ERROR for key: " +
          this.#key +
          "\nThe storage value was malformed when checked against the schema.",
        e
      )
    }
    return value
  }

  getStateSnapshot = () => {
    return this.#value
  }

  setState = (update: StoreUpdate<T>) => {
    let newValue: T
    if (typeof update === "function") {
      newValue = (update as (currentValue: T) => T)(this.#value)
    } else {
      newValue = update
    }
    this.#value = newValue
    const storageValue = JSON.stringify(newValue)
    this.#storage.setItem(this.#key, storageValue)
    window.dispatchEvent(
      new StorageEvent("storage", {
        key: this.#key,
        newValue: storageValue,
        storageArea: this.#storage,
      })
    )
  }

  register = (syncCallback: () => void) => {
    const callback = (e: StorageEvent) => {
      if (e.key === this.#key && e.storageArea === this.#storage) {
        if (e.newValue !== e.oldValue) {
          this.#value = this.#validateStorageValue(e.newValue)
        }
        syncCallback()
      }
    }
    window.addEventListener("storage", callback)
    return () => {
      window.removeEventListener("storage", callback)
    }
  }
}

const emptySymbol = Symbol("empty")

export const useBrowserStore = <T, Selection = T>(
  store: BrowserStore<T>,
  selector?: (state: T) => Selection,
  equalityFn: (a: Selection, b: Selection) => boolean = dequal
) => {
  const lastSelection = React.useRef<Selection | typeof emptySymbol>(
    emptySymbol
  )
  const getSnapshot = React.useCallback(() => {
    const state = store.getStateSnapshot()
    const selection = selector
      ? selector(state)
      : (state as unknown as Selection)
    if (
      lastSelection.current !== emptySymbol &&
      equalityFn(lastSelection.current, selection)
    ) {
      return lastSelection.current
    }
    lastSelection.current = selection
    return selection
  }, [store, selector, equalityFn])
  const value = React.useSyncExternalStore(store.register, getSnapshot)
  const setValue = React.useCallback(
    (update: StoreUpdate<T>) => store.setState(update),
    [store]
  )
  return [value, setValue] as const
}

export const createBrowserStore = <T,>(args: BrowserStoreApi<T>) => {
  return new BrowserStore(args)
}

export const createBrowserStoreProvider = <T, I>(
  createStore: (context: I) => BrowserStore<T>
) => {
  const context = React.createContext<BrowserStore<T> | null>(null)

  const StoreProvider = (props: { children?: React.ReactNode; context: I }) => {
    const storeRef = React.useRef<BrowserStore<T> | null>(null)
    if (storeRef.current === null) {
      storeRef.current = createStore(props.context)
    }
    return (
      <context.Provider value={storeRef.current}>
        {props.children}
      </context.Provider>
    )
  }

  const useStore = <Selection = T>(
    selector?: (state: T) => Selection,
    equalityFn?: (a: Selection, b: Selection) => boolean
  ) => {
    const store = React.useContext(context)
    if (!store) {
      throw new Error("no store provider for browser store")
    }
    return useBrowserStore(store, selector, equalityFn)
  }

  return {
    StoreProvider,
    useStore,
  }
}
